import {
  default as superAgentRequest,
  ProgressEvent,
  SuperAgentStatic,
  SuperAgentRequest,
  Response,
} from 'superagent';

import {getGlobalRequestHandlers} from './utilities/globalRequestHandlers';

export type RequestHeaders = {
  [header: string]: string;
};
export type RequestHeadersHandler = (headers: RequestHeaders) => RequestHeaders;

export type ConfigureAgentHandler = (
  agent: SuperAgentStatic
) => SuperAgentStatic;

export type ConfigureRequestHandler = (
  agent: SuperAgentRequest
) => SuperAgentRequest;

export type CallbackHandler = (err: any, res?: Response) => void;

export type BadCredentialsCondition = {
  conditionType: 'BadCredentialsCondition';
};

export type EmailAccountInResponse = {
  authenticationMode: 'USERNAME_PASSWORD' | 'OAUTH';
  displayName: string;
  emailAddress: string;
  id: string;
  state: 'INIT' | 'ACTIVE' | 'INACTIVE' | 'AUTHENTICATION_FAILURE';
};

export type GetEmailAccountsResponse = {
  emailAccounts: Array<EmailAccountInResponse>;
};

export type GetOauthParamsResponse = {
  authenticationUrl: string;
  clientId: string;
  emailAccountId: string;
  redirectUri: string;
  scope: string;
};

export type GroupInResponse = {
  id: string;
  name: string;
};

export type LoggedInUserInformation = {
  digitalSigningActivated: boolean;
  displayName: string;
  documentSigningOptions: 'NONE' | 'SIGNING' | 'SIGNING_NO_PASSPHRASE';
  givenNames: string;
  lastNames: string;
  locale: string;
  permissions: Array<string>;
  timesheetId: string;
  userId: string;
  username: string;
};

export type Money = {
  amount: string;
};

export type PageItemUserAndGroupsInResponse = {
  cursor: string;
  item: UserAndGroupsInResponse;
};

export type PageItemUserInResponse = {
  cursor: string;
  item: UserInResponse;
};

export type PageUserAndGroupsInResponse = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemUserAndGroupsInResponse>;
};

export type PageUserInResponse = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemUserInResponse>;
};

export type PrivasphereAccount = {
  emailAddress: string;
  privasphereAccountId: string;
};

export type PrivasphereAccountParams = {
  emailAddress: string;
  password: string;
};

export type RenewOauthTokensRequest = {
  authorizationCode: string;
};

export type SearchUserAndGroupResponse = {
  items: PageUserAndGroupsInResponse;
};

export type SearchUserResponse = {
  users: PageUserInResponse;
};

export type SetEmailAccountPasswordParams = {
  password: string;
};

export type UserAndGroupsInResponse = {
  id: string;
  name: string;
  type: 'USER' | 'GROUP';
};

export type UserInResponse = {
  active: boolean;
  displayName: string;
  id: string;
  initialLetters: string;
};

export type UserLocaleUpdateParams = {
  locale: string;
};

export type UserNameUpdateParams = {
  givenNames: string;
  lastNames: string;
};

export type UserSettings = {
  defaultBillability?: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
  defaultHourlyRate: Money;
  emailAddress: string;
  givenNames: string;
  initials: string;
  lastNames: string;
  locale: string;
  officeLocationId: string;
  signatureDescription: string;
  username: string;
};

export type UserSignatureDescriptionUpdateParams = {
  signatureDescription: string;
};

export type Response_getGroups_200 = Array<GroupInResponse>;

export type Response_logoutUser_200 = string;

export type Response_getPrivasphereAccounts_200 = Array<PrivasphereAccount>;

export type Response_getSignatureImage_200 = {};

export interface ResponseWithBody<S extends number, T> extends Response {
  status: S;
  body: T;
}

export type QueryParameters = {[param: string]: any};

export interface CommonRequestOptions {
  $queryParameters?: QueryParameters;
  $domain?: string;
  $path?: string | ((path: string) => string);
  $progressCallback?: (arg0: ProgressEvent) => void;
  $retries?: number;
  $timeout?: number;
  $deadline?: number;
  $signal?: AbortSignal;
}

type SuperAgentError = {status: number};
const isSuperAgentError = (error: unknown): error is SuperAgentError =>
  Boolean((error as SuperAgentError).status);

/*
 *
 * @class Backend
 * @param {(string)} [domainOrOptions] - The project domain.
 */
export class Backend {
  private domain: string = '';
  private onUnauthorizedHandler?: CallbackHandler;
  private errorHandlers: CallbackHandler[] = [];
  private requestHeadersHandler?: RequestHeadersHandler;
  private configureAgentHandler?: ConfigureAgentHandler;
  private configureRequestHandler?: ConfigureRequestHandler;

  public constructor(domain?: string, onUnauthorizedHandler?: CallbackHandler) {
    if (domain) {
      this.domain = domain;
    }

    if (onUnauthorizedHandler) {
      this.onUnauthorizedHandler = onUnauthorizedHandler;
    }

    this.getEmailAccountsURL = this.getEmailAccountsURL.bind(this);
    this.getEmailAccounts = this.getEmailAccounts.bind(this);
    this.getOauthParamsURL = this.getOauthParamsURL.bind(this);
    this.getOauthParams = this.getOauthParams.bind(this);
    this.gatherOauthTokensURL = this.gatherOauthTokensURL.bind(this);
    this.gatherOauthTokens = this.gatherOauthTokens.bind(this);
    this.setEmailAccountPasswordURL =
      this.setEmailAccountPasswordURL.bind(this);
    this.setEmailAccountPassword = this.setEmailAccountPassword.bind(this);
    this.getGroupsURL = this.getGroupsURL.bind(this);
    this.getGroups = this.getGroups.bind(this);
    this.getLoggedInUserInformationURL =
      this.getLoggedInUserInformationURL.bind(this);
    this.getLoggedInUserInformation =
      this.getLoggedInUserInformation.bind(this);
    this.getUserSettingsURL = this.getUserSettingsURL.bind(this);
    this.getUserSettings = this.getUserSettings.bind(this);
    this.updateUserNameURL = this.updateUserNameURL.bind(this);
    this.updateUserName = this.updateUserName.bind(this);
    this.updateUserLocaleURL = this.updateUserLocaleURL.bind(this);
    this.updateUserLocale = this.updateUserLocale.bind(this);
    this.updateUserSignatureDescriptionURL =
      this.updateUserSignatureDescriptionURL.bind(this);
    this.updateUserSignatureDescription =
      this.updateUserSignatureDescription.bind(this);
    this.logoutUserURL = this.logoutUserURL.bind(this);
    this.logoutUser = this.logoutUser.bind(this);
    this.getPrivasphereAccountsURL = this.getPrivasphereAccountsURL.bind(this);
    this.getPrivasphereAccounts = this.getPrivasphereAccounts.bind(this);
    this.createPrivasphereAccountURL =
      this.createPrivasphereAccountURL.bind(this);
    this.createPrivasphereAccount = this.createPrivasphereAccount.bind(this);
    this.deletePrivasphereAccountURL =
      this.deletePrivasphereAccountURL.bind(this);
    this.deletePrivasphereAccount = this.deletePrivasphereAccount.bind(this);
    this.updatePrivasphereAccountURL =
      this.updatePrivasphereAccountURL.bind(this);
    this.updatePrivasphereAccount = this.updatePrivasphereAccount.bind(this);
    this.deleteSignatureImageURL = this.deleteSignatureImageURL.bind(this);
    this.deleteSignatureImage = this.deleteSignatureImage.bind(this);
    this.getSignatureImageURL = this.getSignatureImageURL.bind(this);
    this.getSignatureImage = this.getSignatureImage.bind(this);
    this.setSignatureImageURL = this.setSignatureImageURL.bind(this);
    this.setSignatureImage = this.setSignatureImage.bind(this);
    this.searchUsersURL = this.searchUsersURL.bind(this);
    this.searchUsers = this.searchUsers.bind(this);
    this.searchUsersAndGroupsURL = this.searchUsersAndGroupsURL.bind(this);
    this.searchUsersAndGroups = this.searchUsersAndGroups.bind(this);
  }

  public getDomain(): string {
    return this.domain;
  }

  public addErrorHandler(handler: CallbackHandler): void {
    this.errorHandlers.push(handler);
  }

  public setRequestHeadersHandler(handler: RequestHeadersHandler): void {
    this.requestHeadersHandler = handler;
  }

  public setConfigureAgentHandler(handler: ConfigureAgentHandler): void {
    this.configureAgentHandler = handler;
  }

  public setConfigureRequestHandler(handler: ConfigureRequestHandler): void {
    this.configureRequestHandler = handler;
  }

  public request(
    method: string,
    url: string,
    body: any,
    headers: RequestHeaders,
    form: any,
    reject: CallbackHandler,
    resolve: CallbackHandler,
    progress: CallbackHandler,
    opts: CommonRequestOptions,
    signal?: AbortSignal,
    skipRedirectOnUnauthorized = false
  ): void {
    const agent = this.configureAgentHandler
      ? this.configureAgentHandler(superAgentRequest)
      : superAgentRequest;

    if (process.env.NODE_ENV === 'test') {
      throw new Error(
        'Calling endpoints in test environment should be avoided, this endpoint needs to be mocked: ' +
          url +
          ' [' +
          method +
          ']'
      );
    }

    let request = agent(method, url);
    if (this.configureRequestHandler) {
      request = this.configureRequestHandler(request);
    }

    let processedHeaders = headers;
    if (this.requestHeadersHandler) {
      processedHeaders = this.requestHeadersHandler({...headers});
    }

    if (body) {
      request.send(body);

      if (typeof body === 'object' && !(body.constructor.name === 'Buffer')) {
        processedHeaders['Content-Type'] = 'application/json';
      }
    }

    if (Object.keys(form).length > 0) {
      // do not explicitly set the the content type
      // https://stackoverflow.com/questions/39280438/fetch-missing-boundary-in-multipart-form-data-post#39281156
      delete processedHeaders['content-type'];
      const formData = new FormData();

      Object.keys(form).forEach((key: string) =>
        formData.append(key, form[key])
      );
      request.send(formData);
    }

    request.set(processedHeaders);

    if (opts.$retries && opts.$retries > 0) {
      request.retry(opts.$retries);
    }

    if (
      (opts.$timeout && opts.$timeout > 0) ||
      (opts.$deadline && opts.$deadline > 0)
    ) {
      request.timeout({deadline: opts.$deadline, response: opts.$timeout});
    }

    request
      .on('progress', progress)
      .on('abort', () => reject('Aborted'))
      .ok((res) => res.status <= 400)
      .end((error, response) => {
        // an error will also be emitted for a 4xx and 5xx status code
        // the error object will then have error.status and error.response fields
        // see superagent error handling: https://github.com/visionmedia/superagent/blob/master/docs/index.md#error-handling
        if (error) {
          reject(error);
          if (!skipRedirectOnUnauthorized) {
            this.onUnauthorizedHandler?.(error);
          }
          this.errorHandlers.forEach((handler) => handler(error));
        } else {
          resolve(response);
        }
      });

    if (signal) {
      signal.addEventListener('abort', () => {
        request.abort();
      });
    }
  }

  private static convertParameterCollectionFormat<T>(
    param: T,
    collectionFormat: string | undefined
  ): T | string {
    if (Array.isArray(param) && param.length >= 2) {
      switch (collectionFormat) {
        case 'csv':
          return param.join(',');
        case 'ssv':
          return param.join(' ');
        case 'tsv':
          return param.join('\t');
        case 'pipes':
          return param.join('|');
        default:
          return param;
      }
    }

    return param;
  }

  /*
   *
   * @method
   * @name Backend#getEmailAccounts
   */
  public async getEmailAccounts({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetEmailAccountsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetEmailAccountsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetEmailAccountsResponse>> =
      new Promise((resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getEmailAccountsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetEmailAccountsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetEmailAccountsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetEmailAccountsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetEmailAccountsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getEmailAccountsURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/email-accounts/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getOauthParams
   * @param {string} emailAccountId -
   */
  public async getOauthParams({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      emailAccountId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetOauthParamsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetOauthParamsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetOauthParamsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['emailAccountId'] === 'undefined') {
          reject(new Error('Missing required  parameter: emailAccountId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getOauthParamsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetOauthParamsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetOauthParamsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetOauthParamsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetOauthParamsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getOauthParamsURL(
    parameters: {
      emailAccountId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/email-accounts/{emailAccountId}/oauth-params/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{emailAccountId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['emailAccountId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#gatherOauthTokens
   * @param {} body -
   * @param {string} emailAccountId -
   */
  public async gatherOauthTokens({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: RenewOauthTokensRequest;
      emailAccountId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['emailAccountId'] === 'undefined') {
          reject(new Error('Missing required  parameter: emailAccountId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.gatherOauthTokensURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public gatherOauthTokensURL(
    parameters: {
      body: RenewOauthTokensRequest;
      emailAccountId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/email-accounts/{emailAccountId}/oauth-token/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{emailAccountId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['emailAccountId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setEmailAccountPassword
   * @param {} body -
   * @param {string} emailAccountId -
   */
  public async setEmailAccountPassword({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SetEmailAccountPasswordParams;
      emailAccountId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, BadCredentialsCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, BadCredentialsCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['emailAccountId'] === 'undefined') {
        reject(new Error('Missing required  parameter: emailAccountId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'PUT',
        this.setEmailAccountPasswordURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, BadCredentialsCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, BadCredentialsCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, BadCredentialsCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public setEmailAccountPasswordURL(
    parameters: {
      body: SetEmailAccountPasswordParams;
      emailAccountId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/email-accounts/{emailAccountId}/set-password/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{emailAccountId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['emailAccountId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getGroups
   */
  public async getGroups({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_getGroups_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_getGroups_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, Response_getGroups_200>> =
      new Promise((resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getGroupsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_getGroups_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_getGroups_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_getGroups_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, Response_getGroups_200>,
      header: result.header,
      hasError: true,
    };
  }

  public getGroupsURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/groups/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getLoggedInUserInformation
   */
  public async getLoggedInUserInformation({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: LoggedInUserInformation;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, LoggedInUserInformation> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, LoggedInUserInformation>> =
      new Promise((resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getLoggedInUserInformationURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, LoggedInUserInformation>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          LoggedInUserInformation
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as LoggedInUserInformation,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, LoggedInUserInformation>,
      header: result.header,
      hasError: true,
    };
  }

  public getLoggedInUserInformationURL(
    parameters: CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/logged-in-user/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getUserSettings
   */
  public async getUserSettings({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: UserSettings;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, UserSettings> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, UserSettings>> = new Promise(
      (resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getUserSettingsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, UserSettings>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          UserSettings
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as UserSettings,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, UserSettings>,
      header: result.header,
      hasError: true,
    };
  }

  public getUserSettingsURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/logged-in-user/settings/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateUserName
   * @param {} body -
   */
  public async updateUserName({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: UserNameUpdateParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: UserSettings;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, UserSettings> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, UserSettings>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateUserNameURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, UserSettings>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          UserSettings
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as UserSettings,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, UserSettings>,
      header: result.header,
      hasError: true,
    };
  }

  public updateUserNameURL(
    parameters: {
      body: UserNameUpdateParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/logged-in-user/settings/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateUserLocale
   * @param {} body -
   */
  public async updateUserLocale({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: UserLocaleUpdateParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: UserSettings;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, UserSettings> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, UserSettings>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateUserLocaleURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, UserSettings>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          UserSettings
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as UserSettings,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, UserSettings>,
      header: result.header,
      hasError: true,
    };
  }

  public updateUserLocaleURL(
    parameters: {
      body: UserLocaleUpdateParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/logged-in-user/settings/locale/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateUserSignatureDescription
   * @param {} body -
   */
  public async updateUserSignatureDescription({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: UserSignatureDescriptionUpdateParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: UserSettings;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, UserSettings> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, UserSettings>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateUserSignatureDescriptionURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, UserSettings>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          UserSettings
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as UserSettings,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, UserSettings>,
      header: result.header,
      hasError: true,
    };
  }

  public updateUserSignatureDescriptionURL(
    parameters: {
      body: UserSignatureDescriptionUpdateParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/user-management/logged-in-user/settings/signature-description/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#logoutUser
   */
  public async logoutUser({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_logoutUser_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_logoutUser_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, Response_logoutUser_200>> =
      new Promise((resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.logoutUserURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_logoutUser_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_logoutUser_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_logoutUser_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, Response_logoutUser_200>,
      header: result.header,
      hasError: true,
    };
  }

  public logoutUserURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/logout/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getPrivasphereAccounts
   */
  public async getPrivasphereAccounts({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_getPrivasphereAccounts_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, Response_getPrivasphereAccounts_200>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_getPrivasphereAccounts_200>
    > = new Promise((resolve, reject) => {
      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getPrivasphereAccountsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_getPrivasphereAccounts_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_getPrivasphereAccounts_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_getPrivasphereAccounts_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_getPrivasphereAccounts_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getPrivasphereAccountsURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/privasphere-accounts/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createPrivasphereAccount
   * @param {} body -
   */
  public async createPrivasphereAccount({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: PrivasphereAccountParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, BadCredentialsCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, BadCredentialsCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createPrivasphereAccountURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, BadCredentialsCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, BadCredentialsCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, BadCredentialsCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public createPrivasphereAccountURL(
    parameters: {
      body: PrivasphereAccountParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/privasphere-accounts/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deletePrivasphereAccount
   * @param {string} privasphereAccountId -
   */
  public async deletePrivasphereAccount({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      privasphereAccountId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['privasphereAccountId'] === 'undefined') {
          reject(
            new Error('Missing required  parameter: privasphereAccountId')
          );
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deletePrivasphereAccountURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public deletePrivasphereAccountURL(
    parameters: {
      privasphereAccountId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/privasphere-accounts/{privasphereAccountId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{privasphereAccountId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['privasphereAccountId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updatePrivasphereAccount
   * @param {} body -
   * @param {string} privasphereAccountId -
   */
  public async updatePrivasphereAccount({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: PrivasphereAccountParams;
      privasphereAccountId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['privasphereAccountId'] === 'undefined') {
          reject(
            new Error('Missing required  parameter: privasphereAccountId')
          );
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updatePrivasphereAccountURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public updatePrivasphereAccountURL(
    parameters: {
      body: PrivasphereAccountParams;
      privasphereAccountId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/privasphere-accounts/{privasphereAccountId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{privasphereAccountId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['privasphereAccountId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteSignatureImage
   */
  public async deleteSignatureImage({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteSignatureImageURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteSignatureImageURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/signature-image/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getSignatureImage
   */
  public async getSignatureImage({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_getSignatureImage_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_getSignatureImage_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_getSignatureImage_200>
    > = new Promise((resolve, reject) => {
      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getSignatureImageURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_getSignatureImage_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_getSignatureImage_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_getSignatureImage_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_getSignatureImage_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getSignatureImageURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/signature-image/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setSignatureImage
   * @param {file} file -
   */
  public async setSignatureImage({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      file: {};
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        headers['content-type'] = 'multipart/form-data';

        if (typeof parameters['file'] !== 'undefined') {
          form['file'] = parameters['file'];
        }

        if (typeof parameters['file'] === 'undefined') {
          reject(new Error('Missing required  parameter: file'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.setSignatureImageURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public setSignatureImageURL(
    parameters: {
      file: {};
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/signature-image/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#searchUsers
   * @param {boolean} excludeCurrentUser -
   * @param {array} ids -
   * @param {boolean} isActive -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   */
  public async searchUsers({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      excludeCurrentUser?: boolean;
      ids?: Array<string>;
      isActive?: boolean;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SearchUserResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SearchUserResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SearchUserResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.searchUsersURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SearchUserResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SearchUserResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SearchUserResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SearchUserResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public searchUsersURL(
    parameters: {
      excludeCurrentUser?: boolean;
      ids?: Array<string>;
      isActive?: boolean;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/users/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['excludeCurrentUser'] !== 'undefined') {
      queryParameters['excludeCurrentUser'] =
        Backend.convertParameterCollectionFormat(
          parameters['excludeCurrentUser'],
          ''
        );
    }

    if (typeof parameters['ids'] !== 'undefined') {
      queryParameters['ids'] = Backend.convertParameterCollectionFormat(
        parameters['ids'],
        ''
      );
    }

    if (typeof parameters['isActive'] !== 'undefined') {
      queryParameters['isActive'] = Backend.convertParameterCollectionFormat(
        parameters['isActive'],
        ''
      );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#searchUsersAndGroups
   * @param {boolean} excludeCurrentUser -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   */
  public async searchUsersAndGroups({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      excludeCurrentUser?: boolean;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SearchUserAndGroupResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SearchUserAndGroupResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SearchUserAndGroupResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.searchUsersAndGroupsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SearchUserAndGroupResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SearchUserAndGroupResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SearchUserAndGroupResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SearchUserAndGroupResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public searchUsersAndGroupsURL(
    parameters: {
      excludeCurrentUser?: boolean;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/user-management/users-and-groups/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['excludeCurrentUser'] !== 'undefined') {
      queryParameters['excludeCurrentUser'] =
        Backend.convertParameterCollectionFormat(
          parameters['excludeCurrentUser'],
          ''
        );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }
}

const backend = new Backend(
  process.env.REACT_APP_SERVER_URL || '/api',
  (error) => {
    if (error.status === 401) {
      window.location.href =
        '/login/?redirect=' +
        encodeURIComponent(
          window.location.pathname +
            window.location.search +
            window.location.hash
        );
    }
  }
);

backend.setConfigureRequestHandler(
  (request: SuperAgentRequest): SuperAgentRequest => {
    if (new RegExp('/download', 'u').test(request.url)) {
      request.responseType('blob');
    }

    return request;
  }
);

export default backend;
